import api from './api';
import type { 
  SearchParams, 
  SearchResponse, 
  SearchSuggestion, 
  SearchHistoryItem 
} from '../types/search';

// Debouncing mechanism to prevent multiple simultaneous API calls
let facetsRequestPromise: Promise<any> | null = null;

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function withRetry<T>(fn: () => Promise<T>, retries = 2, baseDelayMs = 300): Promise<T> {
  let attempt = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      return await fn();
    } catch (error: any) {
      const status = error?.response?.status;
      const retriable = !status || status >= 500; // network/5xx
      if (attempt >= retries || !retriable) throw error;
      await delay(baseDelayMs * Math.pow(2, attempt));
      attempt += 1;
    }
  }
}

export const searchService = {
  // Get available facets for filtering
  async getFacets(): Promise<any> {
    // If there's already a request in progress, return the existing promise
    if (facetsRequestPromise) {
      console.log('Facets request already in progress, returning existing promise');
      return facetsRequestPromise;
    }
    
    // Create new request
    facetsRequestPromise = (async () => {
      try {
        console.log('Making facets API call...');
        const response = await withRetry(() => api.get('/api/documents/facets'));
        return response.data.facets;
      } catch (error: any) {
        console.error('Failed to get facets:', error);
        return {
          document_type: {},
          court: {},
          publisher: {},
          file_type: {}
        };
      } finally {
        facetsRequestPromise = null;
      }
    })();
    
    return facetsRequestPromise;
  },

  // Basic search - using the existing /api/documents/search endpoint
  async search(params: SearchParams): Promise<SearchResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('q', params.query);
    queryParams.append('page', params.page.toString());
    queryParams.append('per_page', params.per_page.toString());
    
    // Add filters
    if (params.filters.document_type) {
      queryParams.append('document_type', params.filters.document_type);
    }
    if (params.filters.court) {
      queryParams.append('court', params.filters.court);
    }
    if (params.filters.publisher) {
      queryParams.append('publisher', params.filters.publisher);
    }
    if (params.filters.file_type) {
      queryParams.append('file_type', params.filters.file_type);
    }
    if (params.filters.date_from) {
      queryParams.append('date_from', params.filters.date_from);
    }
    if (params.filters.date_to) {
      queryParams.append('date_to', params.filters.date_to);
    }

    // Add sort and order
    if (params.sort) {
      queryParams.append('sort', params.sort);
    }
    if (params.order) {
      queryParams.append('order', params.order);
    }

    const response = await withRetry(() => api.get(`/api/documents/search?${queryParams.toString()}`));
    
    // Transform the response to match our expected format
    const data = response.data;
    return {
      documents: data.documents.map((doc: any) => ({
        ...doc,
        solr_id: doc.id?.toString() || '',
        file_path: doc.file_path || '',
        file_name: doc.file_name || '',
        uploader_id: doc.uploader_id || 0,
        uploader_name: doc.uploader_name || '',
        is_indexed: doc.is_indexed || false,
        created_at: doc.created_at || '',
        updated_at: doc.updated_at || '',
        highlights: doc.highlights || {}
      })),
      total: data.total_found || data.total || 0,
      facets: data.facets || {
        document_type: {},
        court: {},
        publisher: {},
        file_type: {}
      },
      pagination: data.pagination || {
        page: params.page,
        per_page: params.per_page,
        total_pages: Math.ceil((data.total_found || data.total || 0) / params.per_page)
      }
    };
  },

  // Advanced search - using the same endpoint with additional parameters
  async advancedSearch(params: SearchParams): Promise<SearchResponse> {
    return this.search(params);
  },

  // Get search suggestions - for now, return empty array since this endpoint doesn't exist
  async getSuggestions(query: string): Promise<SearchSuggestion[]> {
    // TODO: Implement when backend supports it
    return [];
  },

  // Get search history - for now, return empty since this endpoint doesn't exist
  async getSearchHistory(page: number = 1, per_page: number = 20): Promise<{
    history: SearchHistoryItem[];
    pagination: {
      page: number;
      per_page: number;
      total: number;
      pages: number;
    };
  }> {
    // TODO: Implement when backend supports it
    return {
      history: [],
      pagination: {
        page,
        per_page,
        total: 0,
        pages: 0
      }
    };
  },

  // Clear search history - for now, do nothing since this endpoint doesn't exist
  async clearSearchHistory(): Promise<void> {
    // TODO: Implement when backend supports it
  }
};
